import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, TrendingUp, ShoppingBag, Wallet, Boxes, Users2, AlertTriangle,
  ArrowRight, ShoppingCart, ClipboardList, BookText, Coins, Truck, ClipboardCheck,
  Trash2, UserCircle, CalendarClock, Award, BarChart3, PieChart, Settings, MessageSquareText, Landmark,
  FileWarning, Leaf, Clock,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rupiah, num } from "@/lib/format";
import { Card, StatCard, Loading, PageHeader } from "@/components/common";

const ALERT_TONE = {
  danger: "bg-rose-50 border-rose-200 text-rose-700",
  warning: "bg-amber-50 border-amber-200 text-amber-700",
  info: "bg-sky-50 border-sky-200 text-sky-700",
};

// Pintasan "Aksi Cepat" (mirror Owner Command Center) — difilter berdasar hak akses
const QUICK = [
  { icon: ShoppingCart, label: "Penjualan Baru", to: "/pos", perm: ["pos", "create"] },
  { icon: ClipboardList, label: "Order Center", to: "/order-center", perm: ["orders", "view"] },
  { icon: BookText, label: "Kas Harian", to: "/cashbook", perm: ["cash", "view"] },
  { icon: Coins, label: "Petty Cash", to: "/cashbook", perm: ["cash", "create"] },
  { icon: Landmark, label: "Pengeluaran Akunting", to: "/accounting", perm: ["accounting_expenses", "view"] },
  { icon: Truck, label: "Barang Masuk", to: "/shipments", perm: ["goods_receiving", "view"] },
  { icon: ClipboardCheck, label: "Stok Opname", to: "/opname", perm: ["stock_opname", "view"] },
  { icon: Trash2, label: "Bunga Rusak", to: "/waste", perm: ["waste", "view"] },
  { icon: MessageSquareText, label: "Order WhatsApp", to: "/whatsapp", perm: ["pos", "create"] },
  { icon: UserCircle, label: "Karyawan", to: "/users", perm: ["employees", "view"] },
  { icon: CalendarClock, label: "Absensi", to: "/attendance", perm: ["attendance", "view"] },
  { icon: Award, label: "Komisi", to: "/commissions", perm: ["payroll", "view"] },
  { icon: BarChart3, label: "Laporan", to: "/reports", perm: ["profit_reports", "view"] },
  { icon: PieChart, label: "Laba Rugi Unit", to: "/reports", perm: ["profit_reports", "view"] },
  { icon: Settings, label: "Pengaturan", to: "/settings", perm: ["settings", "edit"] },
];

export default function Dashboard() {
  const { branch, isManagement, isOwner, can, user } = useAuth();
  const nav = useNavigate();
  const [basic, setBasic] = useState(null);
  const [cc, setCc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const params = branch ? { branch_id: branch.id } : {};
        const b = await api.get("/dashboard", params).catch(() => null);
        if (alive) setBasic(b);
        if (isManagement) {
          const d = await api.get("/owner/command-center", params).catch(() => null);
          if (alive) setCc(d);
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [branch, isManagement]);

  if (loading) return <Loading />;

  const quick = QUICK.filter((q) => isOwner || can(q.perm[0], q.perm[1]));
  const hour = new Date().getHours();
  const greet = hour < 11 ? "Selamat pagi" : hour < 15 ? "Selamat siang" : hour < 18 ? "Selamat sore" : "Selamat malam";
  const alerts = cc?.alerts || [];

  return (
    <>
      <PageHeader title={`${greet}, ${user?.name?.split(" ")[0] || ""}`} subtitle={branch?.name} icon={LayoutDashboard} />

      {alerts.length > 0 && (
        <div className="space-y-2 mb-6">
          {alerts.map((a, i) => (
            <div key={i} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium ${ALERT_TONE[a.level] || ALERT_TONE.info}`}>
              <AlertTriangle size={16} className="shrink-0" /> {a.text}
            </div>
          ))}
        </div>
      )}

      {/* Metrik utama — clickable */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <ClickStat testid="metric-sales" tone="primary" icon={TrendingUp} label="Penjualan Hari Ini"
          value={rupiah(basic?.sales_today)} sub={`${num(basic?.orders_today || 0)} order`} onClick={() => nav("/sales-report")} />
        <ClickStat testid="metric-cash" icon={Wallet} label="Kas Masuk Hari Ini"
          value={rupiah(basic?.cash_in_today)} sub="ketuk untuk Buku Kas" onClick={() => nav("/cashbook")} />
        <ClickStat testid="metric-active-orders" tone="warning" icon={ShoppingBag} label="Order Aktif"
          value={num(basic?.open_orders || 0)} sub="ketuk untuk Order Center" onClick={() => nav("/order-center")} />
        <ClickStat testid="metric-piutang" tone="danger" icon={Wallet} label="Piutang / Sisa DP"
          value={rupiah(basic?.piutang_total)} sub={`${num(basic?.piutang_count || 0)} order`} onClick={() => nav("/receivables")} />
      </div>

      {/* Pintasan Cepat */}
      <Card className="p-5 mb-6">
        <h3 className="font-semibold text-slate-800 mb-4">Pintasan Cepat</h3>
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          {quick.map((q) => (
            <button
              key={q.label}
              onClick={() => nav(q.to)}
              data-testid={`quick-${q.to.replace(/\//g, "")}-${q.label.replace(/\s/g, "")}`}
              className="flex flex-col items-center gap-2 p-3 rounded-xl border border-slate-200 hover:border-primary hover:bg-primary/5 transition"
            >
              <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                <q.icon size={20} />
              </div>
              <span className="text-xs font-medium text-slate-600 text-center leading-tight">{q.label}</span>
            </button>
          ))}
        </div>
      </Card>

      {/* Breakdown pembayaran hari ini */}
      {basic?.by_method_today?.length ? (
        <Card className="p-5 mb-6">
          <h3 className="font-semibold text-slate-800 mb-3">Penjualan per Metode (Hari Ini)</h3>
          <div className="divide-y divide-slate-100">
            {basic.by_method_today.map((m) => (
              <div key={m.method} className="flex items-center justify-between py-2 text-sm">
                <span className="text-slate-500">{m.method}</span>
                <span className="font-medium text-slate-800">{rupiah(m.amount)}</span>
              </div>
            ))}
          </div>
        </Card>
      ) : null}

      {/* Detail Command Center untuk management */}
      {cc && <CommandCenterDetail cc={cc} nav={nav} />}

      {/* Stok menipis (semua role) */}
      {basic?.low_stock?.length ? (
        <Card className="p-5">
          <h3 className="font-semibold text-slate-800 mb-3">Stok Menipis</h3>
          <div className="divide-y divide-slate-100">
            {basic.low_stock.map((s) => (
              <div key={s.item_id} className="flex items-center justify-between py-2 text-sm">
                <span className="text-slate-700">{s.name} <span className="text-slate-400">· {s.kind}</span></span>
                <span className="text-rose-600 font-medium">{s.qty_available} / min {s.min_stock} {s.unit}</span>
              </div>
            ))}
          </div>
        </Card>
      ) : null}
    </>
  );
}

function ClickStat({ label, value, sub, icon: Icon, tone = "default", onClick, testid }) {
  const tones = { default: "text-slate-800", primary: "text-primary", danger: "text-rose-600", warning: "text-amber-600", success: "text-emerald-600" };
  return (
    <button onClick={onClick} data-testid={testid} className="text-left group">
      <Card className="p-5 group-hover:border-primary group-hover:shadow-md transition h-full">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-slate-500">{label}</span>
          <Icon size={18} className="text-slate-400 group-hover:text-primary" />
        </div>
        <div className={`text-2xl font-bold mt-2 ${tones[tone]}`}>{value}</div>
        {sub && <div className="text-xs text-slate-400 mt-1 flex items-center gap-1">{sub} <ArrowRight size={11} className="opacity-0 group-hover:opacity-100 transition" /></div>}
      </Card>
    </button>
  );
}

function CommandCenterDetail({ cc, nav }) {
  const { profit, orders, inventory, cash, employees, units = [], operations } = cc;
  return (
    <div className="grid lg:grid-cols-3 gap-6 mb-6">
      {operations && (
        <Card className="p-5 lg:col-span-3" data-testid="cc-operations">
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2"><Leaf size={17} className="text-primary" /> Operasional Hari Ini</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <OpsCard testid="ops-missing-reports" icon={FileWarning} tone="info"
              label="Belum Lapor Hari Ini" value={`${operations.missing_reports.count} / ${operations.missing_reports.total_staff}`}
              sub={operations.missing_reports.staff.slice(0, 3).map((x) => x.name).join(", ") || "Semua staf sudah lapor"}
              onClick={() => nav("/daily-report")} />
            <OpsCard testid="ops-care-due" icon={Leaf} tone={operations.care.due_count ? "warning" : "success"}
              label="Perawatan Jatuh Tempo" value={`${operations.care.due_count} cabang`}
              sub={operations.care.due_count ? operations.care.branches.map((b) => b.name).slice(0, 3).join(", ") : `Interval ${operations.care.interval_days} hari · aman`}
              onClick={() => nav("/care")} />
            <OpsCard testid="ops-freshness" icon={AlertTriangle} tone={operations.freshness.expired ? "danger" : operations.freshness.warning ? "warning" : "success"}
              label="Bunga Kadaluarsa / Menipis" value={`${operations.freshness.expired} / ${operations.freshness.warning}`}
              sub={`Kadaluarsa ≥${operations.freshness.expire_days}h · menipis ≥${operations.freshness.warn_days}h`}
              onClick={() => nav("/freshness")} />
            <OpsCard testid="ops-overtime" icon={Clock} tone={operations.overtime.count ? "warning" : "success"}
              label="Lembur Menunggu" value={`${operations.overtime.count}`}
              sub={operations.overtime.count ? `${operations.overtime.hours} jam · ketuk untuk setujui` : "Tidak ada pengajuan"}
              onClick={() => nav("/attendance")} />
          </div>
        </Card>
      )}
      <Card className="p-5 lg:col-span-2">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-800">Alur Produksi & Pengiriman</h3>
          <button onClick={() => nav("/order-center")} className="text-sm text-primary font-medium flex items-center gap-1 hover:underline">Order Center <ArrowRight size={14} /></button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            ["Antre Rangkai", orders.to_arrange, "bg-sky-50 text-sky-700"],
            ["Dirangkai", orders.arranging, "bg-amber-50 text-amber-700"],
            ["Siap QC", orders.ready_for_qc, "bg-emerald-50 text-emerald-700"],
            ["QC Gagal", orders.qc_failed, "bg-rose-50 text-rose-700"],
            ["Siap Diambil", orders.ready_pickup, "bg-green-50 text-green-700"],
            ["Siap Kirim", orders.ready_delivery, "bg-sky-50 text-sky-700"],
            ["Dikirim", orders.out_for_delivery, "bg-amber-50 text-amber-700"],
            ["Terlambat", orders.overdue, "bg-rose-50 text-rose-700"],
          ].map(([label, val, cls]) => (
            <div key={label} className={`rounded-xl p-3 ${cls}`}>
              <div className="text-2xl font-bold">{num(val)}</div>
              <div className="text-xs font-medium mt-0.5">{label}</div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-5">
        <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2"><Wallet size={17} /> Kas Hari Ini</h3>
        <div className="space-y-2 text-sm">
          <Row label="Modal Awal" value={rupiah(cash.opening)} />
          <Row label="Penjualan Tunai" value={rupiah(cash.cash_sales)} />
          <Row label="Kas Keluar" value={`- ${rupiah(cash.cash_out)}`} />
          <div className="border-t border-slate-100 my-2" />
          <Row label="Seharusnya" value={rupiah(cash.expected)} bold />
        </div>
      </Card>

      <StatCard label="Laba Bersih (hari ini)" value={rupiah(profit.net_profit)} tone={profit.net_profit >= 0 ? "success" : "danger"} icon={TrendingUp} />
      <Card className="p-5">
        <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2"><Boxes size={16} /> Inventory</h3>
        <div className="grid grid-cols-2 gap-3">
          <Mini label="Stok Menipis" value={inventory.low} tone="text-amber-600" />
          <Mini label="Habis/Minus" value={inventory.out} tone="text-rose-600" />
        </div>
      </Card>
      <Card className="p-5">
        <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2"><Users2 size={16} /> Kehadiran</h3>
        <div className="grid grid-cols-2 gap-3">
          <Mini label="Hadir" value={`${employees.present}/${employees.total}`} tone="text-emerald-600" />
          <Mini label="Terlambat" value={employees.late} tone="text-amber-600" />
        </div>
      </Card>

      {units.length > 0 && (
        <Card className="p-5 lg:col-span-3">
          <h3 className="font-semibold text-slate-800 mb-4">Kinerja Unit (Bulan Berjalan)</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-400 border-b border-slate-100">
                <th className="py-2 font-medium">Unit</th><th className="py-2 font-medium text-right">Omset</th><th className="py-2 font-medium text-right">Laba</th>
              </tr></thead>
              <tbody>
                {units.map((u) => (
                  <tr key={u.unit_id} className="border-b border-slate-50">
                    <td className="py-2.5 font-medium text-slate-700">{u.name}</td>
                    <td className="py-2.5 text-right">{rupiah(u.sales)}</td>
                    <td className={`py-2.5 text-right font-semibold ${u.profit >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{rupiah(u.profit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

const Row = ({ label, value, bold }) => (
  <div className="flex items-center justify-between"><span className="text-slate-500">{label}</span><span className={bold ? "font-semibold text-slate-800" : "text-slate-700"}>{value}</span></div>
);
const Mini = ({ label, value, tone }) => (
  <div className="rounded-xl bg-slate-50 p-3"><div className={`text-lg font-bold ${tone || "text-slate-800"}`}>{value}</div><div className="text-xs text-slate-400 mt-0.5">{label}</div></div>
);
function OpsCard({ label, value, sub, icon: Icon, tone = "info", onClick, testid }) {
  const tones = {
    info: "border-sky-200 bg-sky-50/60 text-sky-700",
    warning: "border-amber-200 bg-amber-50/60 text-amber-700",
    danger: "border-rose-200 bg-rose-50/60 text-rose-700",
    success: "border-emerald-200 bg-emerald-50/60 text-emerald-700",
  };
  return (
    <button onClick={onClick} data-testid={testid} className={`text-left rounded-xl border p-3.5 transition hover:shadow-md ${tones[tone]}`}>
      <div className="flex items-center gap-2 mb-1"><Icon size={16} /> <span className="text-xs font-semibold">{label}</span></div>
      <div className="text-2xl font-bold text-slate-800">{value}</div>
      <div className="text-xs text-slate-500 mt-1 line-clamp-2">{sub}</div>
    </button>
  );
}
